import { useEffect } from 'react';

import reciteIcon from '../reciteme-accessibility-button.png';

// CP-848 — Recite Me assistive toolbar (text-to-speech, translation, visual tools).
//
// Centerline only: PRST has no public-facing side, so it is deliberately not licensed and
// must not load the vendor script. Two independent gates enforce that — the brand check and
// the absence of REACT_APP_RECITE_SERVICE_KEY on PRST/dev builds.
//
// The launcher is a hosted script that renders its own fixed button at the bottom-left of the
// viewport. Nothing else loads until the user clicks it, so users who never launch the toolbar
// send no page content to the vendor.
//
// For users who do launch it, note that text-to-speech is server-side — there is no
// speechSynthesis call anywhere in the bundle; it POSTs to /api/tts and plays back returned
// audio. So text a user has read aloud does reach Recite Me, and they have confirmed
// (2026-09-03) a default retention of three years for cached TTS and translation content,
// with shorter custom retention negotiable. On DQF and expense screens that text is driver
// PII. Config cannot change this; it is a standing InfoSec item, not a bug.
//
// It is viewport-fixed rather than mounted inside the Footer component on purpose: Footer is
// rendered per page in ~60 features and is absent from Login and ViewDQF — the two
// driver-facing screens — so a Footer-anchored button would disappear exactly where it matters.
const LAUNCHER_SRC = 'https://cdn.reciteme.com/reciteme_toolbar_launcher.min.js';
const SCRIPT_ID = 'recite-me-launcher';

export const isReciteMeEnabled = () =>
  process.env.REACT_APP_BRAND === 'Centerline' && !!process.env.REACT_APP_RECITE_SERVICE_KEY;

// There is deliberately no route-change handling here.
//
// Recite Me confirmed (2026-09-03) that the toolbar keeps working as a single-page app
// navigates and does not need re-applying when page content changes. The bundle bears that
// out: Recite.Dom.Observer is on by default (observeDom: true) and is subscribed to
// Recite.Parser:parsed, so it re-parses the wrapper on its own.
//
// Worth knowing what that observer actually does, because the name oversells it. Despite an
// option called useMutations there is no MutationObserver anywhere in the bundle — it runs
// setInterval every 1500ms and compares element.innerHTML.length against the previous length,
// re-parsing only when the number differs. So:
//   * new content becomes readable up to ~1.5s after a route change, not immediately;
//   * a change that happens to produce the exact same character length is not detected;
//   * every detected change re-parses the whole wrapper, which is worth watching on the
//     large Edge Reporting and CommonTable screens.
// If a screen misbehaves, Recite Me support asked to be sent the setup rather than have us
// call an undocumented rescan — a wrong call can double-wrap text nodes.

// Toolbar options, read by the launcher from window.reciteOptions when the user launches it.
// Both settings below override a vendor default that leaks data from this app specifically —
// verified against the toolbar bundle at uswestapi.reciteme.com/asset/js, not assumed:
//
//  * excludeUrlParamsForStats defaults to FALSE, and Recite.Util.getWindowUrlForStats() then
//    POSTs document.location.href — query string included — to events.reciteme.com. This app
//    has /verify-pw-less/dqf?dqf=<ID>&email=<EMAIL>, so the default would ship a driver's
//    email address to their telemetry endpoint. Note their `consent` flag does NOT gate this
//    send; it only suppresses the persistent Recite.User id cookie.
//
//    This flag covers the `uri` field ONLY. Stats.log also sends `referrer`, taken straight
//    from document.referrer with no stripping, and /api/tracking sends the same pair. Verified
//    on staging 2026-09-21: the uri was correctly trimmed while the referrer still carried the
//    full magic-link URL including the driver's email, 16 times in one session. That leak is
//    closed on our side by the strict-origin referrer policy in public/index.html, not here —
//    if that meta tag is ever removed, this returns.
//
//  * Docreader defaults to ENABLED with endpoint usdocreader.reciteme.com. On enable it walks
//    every <a> on the page and rewrites any href matching /\.pdf(\?|#|\/|$)/ to route through
//    that endpoint. S3 presigned URLs match (".pdf" then "?X-Amz-..."), and the DQF screens
//    link documents exactly that way — see ViewDQF/index.js, ClientDQ, CorporateDQ,
//    AssembleNewDQF/NewDocument. Left at its default, launching the toolbar on ViewDQF would
//    hand Recite a presigned URL granting read access to a driver's document for its TTL.
//    Recite Me confirmed (2026-09-03) that disabling it stops any proxying or processing of
//    document links, so this flag is the whole control.
//
// The first two are Recite Me's own recommendation (2026-09-21) for the crash their support
// team reviewed: with the toolbar active, interacting with a react-select dropdown threw
// "NotFoundError: Failed to execute 'removeChild'" and unmounted the whole app. Their default
// HTML parser wraps text nodes in spans to target speech; react-select mounts and unmounts its
// menu constantly, so React went to remove nodes that were no longer where it left them. The
// js parser handles content differently and wrapElements: false stops the wrapping outright.
//
//  * parser / Style.wrapElements — vendor-supplied fix, see CP-848.
//  * excludeUrlParamsForStats / Docreader — our PII controls, described above.
//
// Recite's sample supplied these through a data-options attribute on the script tag. They are
// kept here instead so there is a single source of truth: the launcher merges on-page options
// over data-options (Object.assign, default action mergeLeft), so the two would both apply —
// but the merge is SHALLOW, so splitting config across both places means any future key
// defined in both silently loses one side's nested values.
const TOOLBAR_OPTIONS = {
  parser: 'js',
  Style: { wrapElements: false },
  excludeUrlParamsForStats: true,
  Docreader: { enabled: false },
};

const useReciteMe = () => {
  useEffect(() => {
    if (!isReciteMeEnabled() || document.getElementById(SCRIPT_ID)) return;

    // Must be set before the launcher runs — it reads this when the user opens the toolbar.
    window.reciteOptions = { ...TOOLBAR_OPTIONS, ...(window.reciteOptions || {}) };

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = LAUNCHER_SRC;
    script.async = true;
    script.setAttribute('data-clientid', process.env.REACT_APP_RECITE_SERVICE_KEY);
    // Omitting data-location defaults the vendor to their UK region.
    script.setAttribute('data-location', 'us');
    // data-buttons is deliberately omitted throughout: the vendor's own code generator leaves
    // it off for a toolbar-only launcher, so omitting it matches their supported output.
    script.setAttribute('data-button-toolbar-position', 'floating-bottom-left');
    // Icon-only, matching the sister brand's launcher.
    script.setAttribute('data-button-toolbar-type', 'icon');
    script.setAttribute('data-button-toolbar-type-mobile', 'icon');
    // data-button-toolbar-icon defaults to 'a11yman', one of the launcher's built-in glyphs,
    // which it renders as an inline <svg>. Passing a URL instead takes the other branch and
    // renders a real <img src>, so the artwork stays crisp and is not something CSS has to
    // paint behind their SVG. Webpack resolves the import to a hashed asset URL.
    script.setAttribute('data-button-toolbar-icon', reciteIcon);
    // The toolbar button — alone among the launcher's button types — defaults icon-invert to
    // "true", which stamps
    //   filter: invert(1) sepia(1) saturate(5) hue-rotate(0deg) brightness(1.2)
    // inline on every custom <img>. That is meant to light up a dark monochrome glyph on a
    // coloured button; on full-colour artwork it just recolours it (it was rendering yellow).
    // Parsed as a strict 'true' === comparison, so 'false' switches it off, and doing it here
    // rather than with a CSS override avoids fighting an inline style with !important.
    script.setAttribute('data-button-toolbar-icon-invert', 'false');
    // The background colour defaults to #6B21A8 (purple) even when the attribute is omitted,
    // and is applied as an inline style. The artwork carries its own navy ring, so ask for no
    // fill rather than leaving the vendor default showing behind it.
    script.setAttribute('data-button-toolbar-background-color', 'transparent');
    // Keep the label set even in icon mode: the launcher promotes it to the button's
    // aria-label, so dropping it would leave the control with no accessible name.
    script.setAttribute('data-button-toolbar-text', 'Accessibility Tools');

    document.body.appendChild(script);
  }, []);
};

export default useReciteMe;
